import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SectionHeader } from "@/components/common/Widgets";
import { Package, Plus, Trash2, Edit, Sparkles, Loader2, ShieldCheck, Receipt, IndianRupee, PiggyBank } from "lucide-react";
import { fmtNum } from "@/lib/format";

const CATEGORIES = [
  "PERSONAL", "HOUSING", "MORTGAGE", "GOLD",
  "TWO_WHEELER", "FOUR_WHEELER",
  "SME", "MSME", "BUSINESS_CC", "BUSINESS_TL",
  "EDUCATION", "CONSUMER_DURABLE", "AGRICULTURE", "OTHER",
];

const CONSTITUTIONS = ["Individual", "Proprietorship", "Partnership", "LLP", "Pvt Ltd", "Public Ltd", "HUF", "Trust", "Society"];
const EMPLOYMENT_TYPES = ["Private", "Government", "Self-Employed", "Retired", "Homemaker", "Unemployed"];

export default function LoanProductsPage() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const canEdit = ["HO", "SUPER_ADMIN"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/products");
      setProducts(r.data);
    } catch (e) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const seedDefaults = async () => {
    setSeeding(true);
    try {
      const r = await api.post("/products/seed-defaults");
      if (r.data.seeded) {
        toast.success(`Seeded ${r.data.count} standard products`);
        load();
      } else {
        toast.info(`Skipped: tenant already has ${r.data.existing_count} products`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    } finally { setSeeding(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Deactivate product '${p.name}'?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deactivated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="products-page">
      <SectionHeader
        title="Loan Product Master"
        subtitle="Configure every loan product with amount, tenor, fees, eligibility & risk-based pricing matrix. No code required."
        action={canEdit && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="secondary" onClick={seedDefaults} disabled={seeding} data-testid="product-seed-defaults">
              {seeding ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
              Seed Defaults
            </Button>
            <Button onClick={() => setEditing({})} data-testid="product-new-button"><Plus className="h-4 w-4 mr-1" />New Product</Button>
          </div>
        )}
      />

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">{[0,1,2,3,4,5].map(i => <Skeleton key={i} className="h-44 w-full" />)}</div>
      ) : products.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <Package className="h-12 w-12 text-muted-foreground/40 mx-auto mb-3" />
          <h3 className="text-base font-semibold heading">No products configured yet</h3>
          <p className="text-sm text-muted-foreground mt-1">Click <b>Seed Defaults</b> to get the standard catalog (Personal, Housing, Gold, 2W/4W, SME, Mudra), or <b>New Product</b> to build one from scratch.</p>
          {canEdit && <Button className="mt-4" onClick={seedDefaults} disabled={seeding} data-testid="product-seed-empty"><Sparkles className="h-4 w-4 mr-1" />Seed Defaults</Button>}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {products.map(p => (
            <Card key={p.id} className="p-4 space-y-3" data-testid={`product-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold heading leading-tight">{p.name}</h3>
                  <div className="text-[11px] text-muted-foreground mono">{p.code}</div>
                </div>
                <Badge variant="outline" className="text-[10px]">{p.category}</Badge>
              </div>
              {p.description && <p className="text-xs text-muted-foreground line-clamp-2">{p.description}</p>}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><div className="text-muted-foreground">Amount</div><div className="font-medium tabular">₹{fmtNum(p.min_amount_lakhs,1)} – ₹{fmtNum(p.max_amount_lakhs,1)} L</div></div>
                <div><div className="text-muted-foreground">Tenor</div><div className="font-medium tabular">{p.min_tenor_months}–{p.max_tenor_months} mo</div></div>
                <div><div className="text-muted-foreground">Proc. Fee</div><div className="font-medium tabular">{p.processing_fee_pct}%</div></div>
                <div><div className="text-muted-foreground">Max FOIR</div><div className="font-medium tabular">{p.max_foir_pct}%</div></div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {p.requires_collateral && <Badge variant="outline" className="text-[10px] border-warning/40 text-warning"><ShieldCheck className="h-3 w-3 mr-0.5" />Collateral</Badge>}
                {p.requires_guarantor && <Badge variant="outline" className="text-[10px] border-warning/40 text-warning">Guarantor</Badge>}
                {p.requires_video_kyc && <Badge variant="outline" className="text-[10px] border-accent/40 text-accent">VKYC</Badge>}
                {p.requires_aml_check && <Badge variant="outline" className="text-[10px] border-info/40 text-info">AML</Badge>}
                {!p.active && <Badge variant="outline" className="text-[10px] border-destructive/40 text-destructive">Inactive</Badge>}
              </div>
              {p.roi_matrix?.length > 0 && (
                <div className="border-t border-border pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">ROI Matrix</div>
                  <div className="space-y-0.5">
                    {p.roi_matrix.slice(0, 5).map((s, i) => (
                      <div key={i} className="flex items-center justify-between text-[11px] mono">
                        <span>{s.grade} · {s.label}</span>
                        <span className="text-muted-foreground">{s.min_score}–{s.max_score}</span>
                        <span className="font-semibold tabular">{s.roi_pct > 0 ? `${s.roi_pct}%` : s.decision}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {canEdit && (
                <div className="flex gap-2 pt-2 border-t border-border">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => setEditing(p)} data-testid={`product-edit-${p.id}`}><Edit className="h-3.5 w-3.5 mr-1" />Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(p)} data-testid={`product-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {editing && <ProductEditor product={editing} onClose={() => setEditing(null)} onSave={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ProductEditor({ product, onClose, onSave }) {
  const isNew = !product?.id;
  const [form, setForm] = useState({
    code: product?.code || "",
    name: product?.name || "",
    category: product?.category || "PERSONAL",
    description: product?.description || "",
    loan_type_codes: product?.loan_type_codes?.join(",") || "",
    min_amount_lakhs: product?.min_amount_lakhs ?? 0.5,
    max_amount_lakhs: product?.max_amount_lakhs ?? 25,
    min_tenor_months: product?.min_tenor_months ?? 12,
    max_tenor_months: product?.max_tenor_months ?? 60,
    processing_fee_pct: product?.processing_fee_pct ?? 1.0,
    processing_fee_min: product?.processing_fee_min ?? 500,
    processing_fee_max: product?.processing_fee_max ?? 25000,
    penal_interest_pct: product?.penal_interest_pct ?? 2.0,
    foreclosure_charges_pct: product?.foreclosure_charges_pct ?? 3.0,
    min_age: product?.min_age ?? 21,
    max_age: product?.max_age ?? 60,
    min_monthly_income: product?.min_monthly_income ?? 25000,
    max_foir_pct: product?.max_foir_pct ?? 55.0,
    eligible_constitutions: product?.eligible_constitutions || ["Individual"],
    eligible_employment_types: product?.eligible_employment_types || ["Private", "Government", "Self-Employed"],
    requires_collateral: product?.requires_collateral ?? false,
    requires_guarantor: product?.requires_guarantor ?? false,
    requires_video_kyc: product?.requires_video_kyc ?? true,
    requires_field_verification: product?.requires_field_verification ?? true,
    requires_aml_check: product?.requires_aml_check ?? true,
    requires_esign: product?.requires_esign ?? true,
    requires_estamp: product?.requires_estamp ?? false,
    disbursement_mode: product?.disbursement_mode || "LUMP_SUM",
    roi_matrix: product?.roi_matrix || [
      { grade: "A", label: "Excellent", min_score: 775, max_score: 900, roi_pct: 10.5, decision: "APPROVE" },
      { grade: "B", label: "Good", min_score: 725, max_score: 774, roi_pct: 12.5, decision: "APPROVE" },
      { grade: "C", label: "Medium", min_score: 675, max_score: 724, roi_pct: 15.0, decision: "APPROVE" },
      { grade: "D", label: "High Risk", min_score: 600, max_score: 674, roi_pct: 18.0, decision: "REFER" },
      { grade: "E", label: "Reject", min_score: 300, max_score: 599, roi_pct: 0, decision: "REJECT" },
    ],
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setRoi = (i, k, v) => setForm(f => ({ ...f, roi_matrix: f.roi_matrix.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form, loan_type_codes: form.loan_type_codes.split(",").map(s => s.trim()).filter(Boolean) };
      if (isNew) {
        await api.post("/products", payload);
        toast.success("Product created");
      } else {
        await api.patch(`/products/${product.id}`, payload);
        toast.success("Product updated");
      }
      onSave();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? "New Loan Product" : `Edit — ${product.name}`}</DialogTitle>
          <DialogDescription>All parameters are tenant-scoped. Edits apply to NEW applications only.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="basic">
          <TabsList>
            <TabsTrigger value="basic">Basics</TabsTrigger>
            <TabsTrigger value="eligibility">Eligibility</TabsTrigger>
            <TabsTrigger value="fees">Fees</TabsTrigger>
            <TabsTrigger value="roi">Risk-Based Pricing</TabsTrigger>
            <TabsTrigger value="rules">Rules</TabsTrigger>
          </TabsList>
          <TabsContent value="basic" className="space-y-3 pt-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Code (unique)"><Input value={form.code} onChange={e => set("code", e.target.value)} disabled={!isNew} data-testid="product-code" /></Field>
              <Field label="Category"><Select value={form.category} onValueChange={v => set("category", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></Field>
              <Field label="Display Name" className="col-span-2"><Input value={form.name} onChange={e => set("name", e.target.value)} data-testid="product-name" /></Field>
              <Field label="Description" className="col-span-2"><Input value={form.description} onChange={e => set("description", e.target.value)} /></Field>
              <Field label="Loan Type Codes (comma-separated)" className="col-span-2"><Input value={form.loan_type_codes} onChange={e => set("loan_type_codes", e.target.value)} placeholder="PERSONAL_LOAN, PL_TOPUP" /></Field>
              <Field label="Min Amount (₹ L)"><Input type="number" value={form.min_amount_lakhs} onChange={e => set("min_amount_lakhs", Number(e.target.value))} /></Field>
              <Field label="Max Amount (₹ L)"><Input type="number" value={form.max_amount_lakhs} onChange={e => set("max_amount_lakhs", Number(e.target.value))} /></Field>
              <Field label="Min Tenor (months)"><Input type="number" value={form.min_tenor_months} onChange={e => set("min_tenor_months", Number(e.target.value))} /></Field>
              <Field label="Max Tenor (months)"><Input type="number" value={form.max_tenor_months} onChange={e => set("max_tenor_months", Number(e.target.value))} /></Field>
              <Field label="Disbursement Mode"><Select value={form.disbursement_mode} onValueChange={v => set("disbursement_mode", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="LUMP_SUM">Lump-Sum</SelectItem><SelectItem value="STAGE_WISE">Stage-Wise (Housing)</SelectItem></SelectContent></Select></Field>
            </div>
          </TabsContent>
          <TabsContent value="eligibility" className="space-y-3 pt-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Min Age"><Input type="number" value={form.min_age} onChange={e => set("min_age", Number(e.target.value))} /></Field>
              <Field label="Max Age"><Input type="number" value={form.max_age} onChange={e => set("max_age", Number(e.target.value))} /></Field>
              <Field label="Min Monthly Income (₹)"><Input type="number" value={form.min_monthly_income} onChange={e => set("min_monthly_income", Number(e.target.value))} /></Field>
              <Field label="Max FOIR / DBR (%)"><Input type="number" value={form.max_foir_pct} onChange={e => set("max_foir_pct", Number(e.target.value))} /></Field>
              <Field label="Eligible Constitutions" className="col-span-2">
                <div className="flex flex-wrap gap-1.5">{CONSTITUTIONS.map(c => (
                  <button type="button" key={c} onClick={() => set("eligible_constitutions", form.eligible_constitutions.includes(c) ? form.eligible_constitutions.filter(x => x!==c) : [...form.eligible_constitutions, c])} className={`text-xs px-2 py-1 rounded border ${form.eligible_constitutions.includes(c) ? "bg-accent/15 border-accent text-accent" : "border-border text-muted-foreground hover:bg-muted/40"}`}>{c}</button>
                ))}</div>
              </Field>
              <Field label="Eligible Employment Types" className="col-span-2">
                <div className="flex flex-wrap gap-1.5">{EMPLOYMENT_TYPES.map(e_ => (
                  <button type="button" key={e_} onClick={() => set("eligible_employment_types", form.eligible_employment_types.includes(e_) ? form.eligible_employment_types.filter(x => x!==e_) : [...form.eligible_employment_types, e_])} className={`text-xs px-2 py-1 rounded border ${form.eligible_employment_types.includes(e_) ? "bg-accent/15 border-accent text-accent" : "border-border text-muted-foreground hover:bg-muted/40"}`}>{e_}</button>
                ))}</div>
              </Field>
            </div>
          </TabsContent>
          <TabsContent value="fees" className="space-y-3 pt-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Processing Fee (%)"><Input type="number" step="0.1" value={form.processing_fee_pct} onChange={e => set("processing_fee_pct", Number(e.target.value))} /></Field>
              <Field label="Penal Interest (%)"><Input type="number" step="0.1" value={form.penal_interest_pct} onChange={e => set("penal_interest_pct", Number(e.target.value))} /></Field>
              <Field label="Processing Fee Min (₹)"><Input type="number" value={form.processing_fee_min} onChange={e => set("processing_fee_min", Number(e.target.value))} /></Field>
              <Field label="Processing Fee Max (₹)"><Input type="number" value={form.processing_fee_max} onChange={e => set("processing_fee_max", Number(e.target.value))} /></Field>
              <Field label="Foreclosure Charges (%)"><Input type="number" step="0.1" value={form.foreclosure_charges_pct} onChange={e => set("foreclosure_charges_pct", Number(e.target.value))} /></Field>
            </div>
          </TabsContent>
          <TabsContent value="roi" className="space-y-2 pt-3">
            <p className="text-xs text-muted-foreground">Risk grades are matched by bureau score range. The matched slab's ROI is auto-applied at application creation.</p>
            <div className="space-y-2">
              {form.roi_matrix.map((slab, i) => (
                <div key={i} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`roi-row-${slab.grade}`}>
                  <Input className="col-span-1" value={slab.grade} onChange={e => setRoi(i, "grade", e.target.value)} />
                  <Input className="col-span-3" value={slab.label || ""} onChange={e => setRoi(i, "label", e.target.value)} placeholder="Label" />
                  <Input className="col-span-2" type="number" value={slab.min_score} onChange={e => setRoi(i, "min_score", Number(e.target.value))} placeholder="Min" />
                  <Input className="col-span-2" type="number" value={slab.max_score} onChange={e => setRoi(i, "max_score", Number(e.target.value))} placeholder="Max" />
                  <Input className="col-span-2" type="number" step="0.1" value={slab.roi_pct} onChange={e => setRoi(i, "roi_pct", Number(e.target.value))} placeholder="ROI %" />
                  <Select value={slab.decision} onValueChange={v => setRoi(i, "decision", v)}>
                    <SelectTrigger className="col-span-2"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="APPROVE">Approve</SelectItem><SelectItem value="REFER">Refer</SelectItem><SelectItem value="REJECT">Reject</SelectItem></SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="rules" className="space-y-3 pt-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ToggleRow label="Requires Collateral" checked={form.requires_collateral} onChange={v => set("requires_collateral", v)} />
              <ToggleRow label="Requires Guarantor" checked={form.requires_guarantor} onChange={v => set("requires_guarantor", v)} />
              <ToggleRow label="Requires Video KYC" checked={form.requires_video_kyc} onChange={v => set("requires_video_kyc", v)} />
              <ToggleRow label="Requires Field Verification" checked={form.requires_field_verification} onChange={v => set("requires_field_verification", v)} />
              <ToggleRow label="Requires AML / PEP Check" checked={form.requires_aml_check} onChange={v => set("requires_aml_check", v)} />
              <ToggleRow label="Requires eSign" checked={form.requires_esign} onChange={v => set("requires_esign", v)} />
              <ToggleRow label="Requires eStamp" checked={form.requires_estamp} onChange={v => set("requires_estamp", v)} />
            </div>
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="product-save">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}{isNew ? "Create Product" : "Save Changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, className }) {
  return (
    <div className={`space-y-1.5 ${className || ""}`}>
      <Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
function ToggleRow({ label, checked, onChange }) {
  return (
    <div className="flex items-center justify-between rounded-md border border-border bg-muted/20 p-2.5">
      <span className="text-sm">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
